import { View /* , Page  */ } from "@react-pdf/renderer";
import CheckableTag from "antd/lib/tag/CheckableTag";
import React from "react";

import {
  guard,
  constructDisplayPrice,
  randomNumberGenerator,
} from "utils/general";

import {
  Page,
  Row,
  Col,
  CompanyLogo,
  HeaderRow,
  SectionContainer,
  SectionTitleContainer,
  SectionTitle,
  SectionContentContainer,
  TableOutline,
  TableCell,
  TableCol,
  TableRow,
  TableTitle,
  Text,
} from "./PolicyTable.styles";
import Locale from "react-intl-universal";

const ChineseFontStyle = {
  style: {
    fontSize: 10,
    fontFamily: "ChineseFont",
  },
};

const getColWidthPercentage = (noOfPolicies) => {
  const fullPage = 100;
  // let headerColWidth = 20;
  // let totalColWidth = 10;
  let headerColWidth = 15;
  let totalColWidth = 7;

  if (noOfPolicies === 5 || noOfPolicies === 6) {
    headerColWidth = 15;
    // headerColWidth = 10;
    totalColWidth = 7;
    // totalColWidth = 5;
  }

  if (noOfPolicies > 6) {
    headerColWidth = 9;
    // headerColWidth = 10;
    totalColWidth = 4.5;
    // totalColWidth = 5;
  }

  return {
    headerColWidth: headerColWidth + "%",
    totalColWidth: totalColWidth + "%",
    contentColWidth:
      (fullPage - headerColWidth - totalColWidth) / noOfPolicies + "%",
  };
};

const getHeaderWidthPercentage = (isLandscape) => {
  const fullPage = 100;
  let logoColWidth = 15;

  if (isLandscape) {
    logoColWidth = 10;
  }
  return {
    logoColWidth: logoColWidth + "%",
    infoBoxColWidth: fullPage - logoColWidth + "%",
  };
};

const fillEmptyData = (data) => {
  const defaultNoOfCols = 4;
  const noOfData = data.length;
  const formattedData = [...data];

  if (noOfData < defaultNoOfCols) {
    const lengthDiff = defaultNoOfCols - noOfData;
    for (let i = 0; i < lengthDiff; i++) {
      formattedData.push({
        benefits: {
          planCoverages: [],
          hospitalizationBenefits: [],
          femaleBenefits: [],
        },
        yearlyGuaranteedCashPayment: {},
        medicalCard: {},
        investmentFunds: [],
        nominations: [],
        assignee: [],
        trustee: [],
        contingent: [],
      });
    }
  }
  return formattedData;
};

const formatBenefitTypes = (benefitTypes) =>
  benefitTypes.map((benefit) => {
    if (
      ["planCoverage", "hospitalization", "female", "otherBenefit"].includes(
        benefit.type
      )
    ) {
      return {
        ...benefit,
        getTotalValue: (data) =>
          data.reduce((total, benefits) => {
            let foundCoverages = benefits.filter(
              (cvr) => cvr.type === benefit.value
            );

            if (benefit.type === "otherBenefit") {
              foundCoverages = benefits.filter(
                (cvr) => cvr.label === benefit.code
              );
            }

            const totalAmount = foundCoverages.reduce(
              (total, coverage) => total + coverage.amount,
              0
            );
            return total + guard(() => totalAmount, 0);
          }, 0),
      };
    }
    return benefit;
  });

const joinOtherBenefits = (data) => {
  // Add to support other coverage data
  var othersType = [];
  var othersTypeLabel = [];
  for (let i = 0; i < data.length; i++) {
    for (let j = 0; j < data[i].benefits.planCoverages.length; j++) {
      const coverage = data[i].benefits.planCoverages[j];
      if (coverage.type === "otherBenefit") {
        const label = coverage.label
          .substring(coverage.label.indexOf(")") + 1, coverage.label.length)
          .trim();
        othersType.push({
          code: coverage.label,
          label: label,
          value: label,
          type: coverage.type,
        });
        othersTypeLabel.push(label);
      }
    }
  }

  // Remove duplicates
  othersTypeLabel = [...new Set(othersTypeLabel)];
  othersType = othersType.filter((v, i, a) => {
    const value = JSON.stringify(v);
    return (
      i ===
      othersType.findIndex((obj) => {
        return JSON.stringify(obj) === value;
      })
    );
  });

  return {
    othersType,
    othersTypeLabel,
  };
};

const formatPoliciesToDisplay = (policies, policyStatuses, mode) => {
  const excludingStatuses = policyStatuses
    .filter((status) => !status.isIncludePremiums && !status.isIncludeBenefits)
    .map((status) => status.label);
  const excludingPremiumStatuses = policyStatuses
    .filter((status) => !status.isIncludePremiums)
    .map((status) => status.label);

  return policies.reduce((formattedPolicies, policy) => {
    if (mode === "personal" || mode === "linkedFamilyMember") {
      if (!policy.coveredFor && !excludingStatuses.includes(policy.status)) {
        const currentPolicy = { ...policy };
        if (excludingPremiumStatuses.includes(policy.status)) {
          currentPolicy.yearlyPremium = 0;
        }
        return [...formattedPolicies, currentPolicy];
      }
    } else {
      if (!excludingStatuses.includes(policy.status)) {
        const currentPolicy = { ...policy };
        if (excludingPremiumStatuses.includes(policy.status)) {
          currentPolicy.yearlyPremium = 0;
        }
        return [...formattedPolicies, currentPolicy];
      }
    }

    return formattedPolicies;
  }, []);
};

const Header = ({ data, isLandscape, companyLogo, mode }) => {
  const { logoColWidth, infoBoxColWidth } = getHeaderWidthPercentage(
    isLandscape
  );
  return (
    <HeaderRow style={{ ...(isLandscape && { marginTop: 10 }) }}>
      <Col width={logoColWidth}>
        <CompanyLogo src={companyLogo} />
      </Col>
      <Col width={infoBoxColWidth}>
        <PersonalInfoBox dataSource={data} mode={mode} />
      </Col>
    </HeaderRow>
  );
};

const HeaderCell = ({ title, size, isLongText = false }) => {
  return (
    <TableRow isLongText={isLongText}>
      <TableCell
        size={size}
        header
        isLongText={isLongText}
        {...ChineseFontStyle}
      >
        {title}
      </TableCell>
    </TableRow>
  );
};

const ContentCell = ({ value, disabled, size, isLongText }) => {
  return (
    <TableRow isLongText={isLongText}>
      <TableCell size={size} disabled={disabled} isLongText={isLongText}>
        {value || "-"}
      </TableCell>
    </TableRow>
  );
};

const Table = ({ title, rows = [], data = [], rowRender }) => {
  const noOfPolicies = data.length;
  const {
    headerColWidth,
    totalColWidth,
    contentColWidth,
  } = getColWidthPercentage(noOfPolicies);
  // const cellSize = noOfPolicies <= 4 ? "normal" : "small";
  const cellSize = noOfPolicies <= 4 ? "small" : "small";
  const checkIsPriceValue = (amount) => guard(() => Number(amount), false);

  var dataBuffer = [...data];
  var rowsBuffer = [...rows];
  var keyLongText = [];

  for (let i = 0; i < dataBuffer.length; i++) {
    var policy = dataBuffer[i];

    for (const key in policy) {
      //noOfPolicies <=4
      if (
        typeof policy[key] === "string" &&
        policy[key].length > 48 &&
        // cellSize === "normal" &&
        noOfPolicies <= 4
      ) {
        keyLongText.push(key);
      }

      //noOfPolicies >=5
      if (
        typeof policy[key] === "string" &&
        policy[key].length > 32 &&
        // policy[key].length > 69 - (noOfPolicies - 5) * 6 &&
        cellSize === "small"
      ) {
        keyLongText.push(key);
      }
    }
  }
  keyLongText = [...new Set(keyLongText)];

  for (let i = 0; i < rowsBuffer.length; i++) {
    var targetRow = rowsBuffer[i];

    if (
      (targetRow &&
        keyLongText.filter((x) => x === targetRow.key).length !== 0) ||
      (targetRow && targetRow.label.length > 28)
    ) {
      targetRow.isLongText = true;
    }
  }

  return (
    <>
      {title && <TableTitle size={cellSize}>{title}</TableTitle>}
      <TableOutline>
        <TableCol width={headerColWidth}>
          {rowsBuffer.map((row) => {
            return (
              <HeaderCell
                size={cellSize}
                title={row.label}
                key={row.label}
                isLongText={row.isLongText}
              />
            );
          })}
        </TableCol>
        {dataBuffer.map((data) => (
          <TableCol key={randomNumberGenerator()} width={contentColWidth}>
            {rowRender(data, cellSize)}
          </TableCol>
        ))}
        <TableCol width={totalColWidth}>
          {rowsBuffer.map((row) => {
            const totalDisabled = !row.getTotalValue;
            let value = totalDisabled
              ? "N/A"
              : row.getTotalValue(data, row.getValue);
            if (checkIsPriceValue(value)) {
              value = constructDisplayPrice(value, false);
            }
            return (
              <ContentCell
                key={randomNumberGenerator()}
                size={cellSize}
                value={value}
                disabled={totalDisabled}
                isLongText={row.isLongText}
              />
            );
          })}
        </TableCol>
      </TableOutline>
    </>
  );
};

const getTotalValueByKey = (data = [], keyPath) => {
  const keys = keyPath.split(".");
  return data.reduce((totalValue, data) => {
    const value = guard(
      () => keys.reduce((data, key) => guard(() => data[key], 0), data),
      0
    );
    return totalValue + value;
  }, 0);
};

const PlanDescriptionTable = ({ data = [] }) => {
  const rows = [
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.planName")}</Text>
      ),
      key: "name",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.policyNo")}</Text>
      ),
      key: "policyNumber",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.company")}</Text>
      ),
      key: "company",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.commencementDate")}
        </Text>
      ),
      key: "commencementDate",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.maturityDate")}
        </Text>
      ),
      key: "maturityDate",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.planType")}</Text>
      ),
      key: "type",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.premium")}</Text>
      ),
      key: "premium",
      isPriceValue: true,
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.yearlyPremium")}
        </Text>
      ),
      key: "yearlyPremium",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "yearlyPremium"),
    },
    {
      label: <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.mop")}</Text>,
      key: "paymentMode",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.status")}</Text>
      ),
      key: "status",
    },
  ];

  const render = (data, cellSize) =>
    rows.map((row) => {
      let value = "";
      if (data) {
        value = data[row.key];
        if (!!row.getValue) {
          value = row.getValue(data);
        }
        if (value && row.isPriceValue) {
          value = constructDisplayPrice(value, false);
        }
      }

      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });
  return (
    <Table
      title={
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.planDescription")}
        </Text>
      }
      rows={rows}
      data={data}
      rowRender={render}
    />
  );
};

const BenefitsTable = ({ anotherTitle, title, data = [], types = [] }) => {
  var FilterType = types;

  if (anotherTitle === "Plan Coverage") {
    //remove duplicate lines for otherBenefit
    var FilterType = types.filter(
      (obj, index) =>
        types.findIndex((item) => item.value === obj.value) === index
    );
  }

  const render = (data, cellSize) =>
    FilterType.map((type) => {
      const value = guard(() => {
        if (data.length) {
          var coverages = data.filter((data) => data.type === type.value);
          if (type.type === "waiver") {
            return !!coverages.length ? "Yes" : "No";
          }

          if (type.type === "otherBenefit") {
            coverages = data.filter((data) => data.label === type.code);
          }

          const totalAmount = coverages.reduce(
            (total, coverage) => total + coverage.amount,
            0
          );
          return totalAmount && constructDisplayPrice(totalAmount, false);
        }
        return;
      }, 0);
      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={type.isLongText}
        />
      );
    });

  return (
    <Table title={title} rows={FilterType} data={data} rowRender={render} />
  );
};

const InvestmentFundsTable = ({ data = [] }) => {
  const rows = data.reduce((rows, funds) => {
    funds.forEach((fund) => {
      if (!rows.find((row) => row.label === fund.type.trim())) {
        rows.push({ label: fund.type.trim() });
      }
    });
    return rows;
  }, []);

  const render = (data, cellSize) => {
    return rows.map((row) => {
      const value = guard(() => {
        const coverage = data.find(
          (data) => String(data.type).trim() === String(row.label).trim()
        );
        return constructDisplayPrice(coverage.amount, false);
      }, 0);
      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });
  };

  return rows.length ? (
    <Table
      title="For Investment Plan Only"
      rows={rows}
      data={data}
      rowRender={render}
    />
  ) : null;
};

const YearlyGuaranteedCashPaymentTable = ({ data = [] }) => {
  const rows = [
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.startDate")}</Text>
      ),
      key: "startingDate",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.maturityDate")}
        </Text>
      ),
      key: "maturityDate",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.paymentCoupon")}
        </Text>
      ),
      key: "paymentOrCouponIntervals",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.amount")}</Text>
      ),
      key: "amount",
      checkIsPriceValue: (amount) => guard(() => Number(amount), false),
    },
    // {
    //   label: "Funds Estimate at 20 years",
    //   key: "fundsAt20YearsAmt",
    //   isPriceValue: true,
    // },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.fundsFinalMaturityAtAge")}
        </Text>
      ),
      key: "fundsMaturity.age",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.estFundMaturity")}
        </Text>
      ),
      key: "fundsMaturity.estimateFunds",
      isPriceValue: true,
      getTotalValue: () =>
        getTotalValueByKey(data, "fundsMaturity.estimateFunds"),
    },
  ];

  const render = (data, cellSize) =>
    rows.map((row) => {
      let value = row.key
        .split(".")
        .reduce((data, key) => data && data[key], data);
      if (
        value &&
        (row.isPriceValue ||
          (!!row.checkIsPriceValue && row.checkIsPriceValue(value)))
      ) {
        value = constructDisplayPrice(value, false);
      }
      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });

  return (
    <Table
      title={
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.yearlyGuaranteeCashPayment")}
        </Text>
      }
      rows={rows}
      data={data}
      rowRender={render}
    />
  );
};

const MedicalCardTable = ({ data = [] }) => {
  var rows = [
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.name")}</Text>
      ),
      key: "name",
    },
    {
      label: <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.rab")}</Text>,
      key: "roomAndBoardAmt",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "roomAndBoardAmt"),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.lifetimeLimit")}
        </Text>
      ),
      key: "lifetimeLimitAmt",
      getValue: (data) =>
        data["lifetimeLimitAmt"] < 0 ? "Unlimited" : data["lifetimeLimitAmt"],
      checkIsPriceValue: (amount) => guard(() => Number(amount), false),
      getTotalValue: () =>
        !!data.find(
          (medicalCard) => medicalCard && medicalCard.lifetimeLimitAmt < 0
        ) ? (
          <Text {...ChineseFontStyle}>
            {Locale.get("pdfDownload.unlimited")}
          </Text>
        ) : (
          getTotalValueByKey(data, "lifetimeLimitAmt")
        ),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.yearlyLimit")}
        </Text>
      ),
      key: "yearlyLimitAmt",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "yearlyLimitAmt"),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.outpatientCancerTreat")}
        </Text>
      ),
      key: "outpatientCancerTreatmentAmt",
      isPriceValue: true,
      getTotalValue: () =>
        getTotalValueByKey(data, "outpatientCancerTreatmentAmt"),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.outpatientKidneyDialysisTreat")}
        </Text>
      ),
      key: "outpatientKidneyDialysisTreatmentAmt",
      isPriceValue: true,
      getTotalValue: () =>
        getTotalValueByKey(data, "outpatientKidneyDialysisTreatmentAmt"),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.deductible")}
        </Text>
      ),
      key: "deductible",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "deductible"),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.coInsurance")}
        </Text>
      ),
      key: "coInsurance",
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.remark")}</Text>
      ),
      key: "remarks",
    },
  ];

  const render = (data, cellSize) =>
    rows.map((row) => {
      let value = "";
      if (data) {
        value = data[row.key];
        if (!!row.getValue) {
          value = row.getValue(data);
        }
        if (
          value &&
          (row.isPriceValue ||
            (!!row.checkIsPriceValue && row.checkIsPriceValue(value)))
        ) {
          value = constructDisplayPrice(value, false);
        }
      }

      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });

  return (
    <Table
      title={
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.medCard")}</Text>
      }
      rows={rows}
      data={data}
      rowRender={render}
    />
  );
};

const NominationTable = ({ data = [], type, label }) => {
  const Data = [...data];
  const FilterData = [];

  for (let i = 0; i < Data.length; i++) {
    if (Data[i]) {
      FilterData.push(Data[i]);
    }
  }

  const sortedData = FilterData.map((data) =>
    data.sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
  );

  const nominationTypes = {
    BENEFICIARY: { label: "Beneficiary", key: "name" },
    TRUSTEE: { label: "Trustee", key: "name" },
    CONTINGENT: { label: "Contingent Owner", key: "name" },
    ASSIGNEE: { label: "Assignee", key: "assigneeName" },
  };

  var maxRow = 0;
  sortedData.forEach((nominationArrays, data) => {
    const dataLength = nominationArrays.length;
    if (dataLength > maxRow) {
      maxRow = dataLength;
    }
  });

  var rows = [];
  for (let i = 0; i < maxRow; i++) {
    rows.push(nominationTypes[type]);
  }

  const render = (nominees, cellSize) => {
    let idx = 0;
    let nominee = nominees[idx];
    return rows.map((row) => {
      let value = "";
      if (nominee && nominee[row.key]) {
        value = nominee[row.key];
        idx++;
        nominee = nominees[idx];
      }
      return (
        <ContentCell
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });
  };

  return (
    <Table title={label} rows={rows} data={sortedData} rowRender={render} />
  );
};

const APLPolicyLoanTable = ({ data = [] }) => {
  const rows = [
    {
      label: <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.apl")}</Text>,
      key: "apl",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "apl.amount"),
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.policyLoan")}
        </Text>
      ),
      key: "policyLoan",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "policyLoan.amount"),
    },
  ];

  const render = (data, cellSize) =>
    rows.map((row) => {
      let value = guard(() => data[row.key].amount);
      if (value && row.isPriceValue) {
        value = constructDisplayPrice(value, false);
      }
      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });

  return (
    <Table
      title={
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.aplPolicy")}</Text>
      }
      rows={rows}
      data={data}
      rowRender={render}
    />
  );
};

const SurrenderValueTable = ({ data = [] }) => {
  const rows = [
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.guaranteedSurrenderValue")}
        </Text>
      ),
      key: "guaranteedValue",
      isPriceValue: true,
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.fundValue")}</Text>
      ),
      key: "dividend",
      isPriceValue: true,
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.regularCashPayout")}
        </Text>
      ),
      key: "regularCashPayout",
      isPriceValue: true,
    },
    {
      label: (
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.terminalDiv")}
        </Text>
      ),
      key: "terminalDividend",
      isPriceValue: true,
    },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.total")}</Text>
      ),
      key: "total",
      isPriceValue: true,
      getTotalValue: () => getTotalValueByKey(data, "surrenderValue.total"),
    },
    { label: "", key: "", getValue: () => " " },
    {
      label: (
        <Text {...ChineseFontStyle}>{Locale.get("pdfDownload.total")}</Text>
      ),
      key: "",
      isPriceValue: true,
      getValue: ({ apl = 0, policyLoan = 0, surrenderValue }) =>
        guard(() => surrenderValue.total, 0) -
        (apl.amount || 0) -
        (policyLoan.amount || 0),
      getTotalValue: (data, getValue) =>
        data.reduce((total, policy) => total + getValue(policy), 0),
    },
  ];

  const render = (data, cellSize) => {
    const { surrenderValue } = data;
    return rows.map((row) => {
      let value = guard(() =>
        row.key === "total"
          ? surrenderValue[row.key]
          : surrenderValue[row.key].amount
      );
      if (surrenderValue) {
        if (!!row.getValue) {
          value = row.getValue(data);
        }
        if (value && row.isPriceValue) {
          value = constructDisplayPrice(value, false);
        }
      }
      return (
        <ContentCell
          key={randomNumberGenerator()}
          value={value}
          size={cellSize}
          isLongText={row.isLongText}
        />
      );
    });
  };

  //remove duplicate total lines
  rows.pop();
  rows.pop();
  return (
    <Table
      title={
        <Text {...ChineseFontStyle}>
          {Locale.get("pdfDownload.surrenderFundValue")}
        </Text>
      }
      rows={rows}
      data={data}
      rowRender={render}
    />
  );
};

const PersonalInfoBox = ({ dataSource, mode }) => {
  return (
    <SectionContainer>
      <SectionTitleContainer>
        <SectionTitle>
          {mode === "personal" ? (
            <Text {...ChineseFontStyle}>
              {Locale.get("pdfDownload.personalDependentInfo")}
            </Text>
          ) : mode === "linkedFamilyMember" ? (
            <Text {...ChineseFontStyle}>
              {Locale.get("pdfDownload.linkedFamilyMemberInfo")}
            </Text>
          ) : (
            <Text {...ChineseFontStyle}>
              {Locale.get("pdfDownload.coveredForInfo")}
            </Text>
          )}
        </SectionTitle>
      </SectionTitleContainer>
      <SectionContentContainer>
        {dataSource.map((data) => (
          <Col key={data.label}>
            <Row>
              <Text {...ChineseFontStyle}>{data.label}</Text>
              <Text {...ChineseFontStyle}>{`:   ${data.value || "-"}`}</Text>
            </Row>
          </Col>
        ))}
      </SectionContentContainer>
    </SectionContainer>
  );
};

const PolicyTable = ({
  data,
  benefitTypes,
  policyStatuses,
  personalInfoData,
  companyLogo,
  mode,
}) => {
  let formattedBenefitTypes = benefitTypes;
  const policiesToDisplay = formatPoliciesToDisplay(data, policyStatuses, mode);

  // const orientation = policiesToDisplay.length > 4 ? "landscape" : "portrait";
  const orientation = "portrait";
  const formattedData = fillEmptyData(policiesToDisplay);

  const { othersType, othersTypeLabel } = joinOtherBenefits(data);

  formattedBenefitTypes.push(...othersType);
  formattedBenefitTypes = formatBenefitTypes(benefitTypes);

  const checkFieldExistInPolicy = (data, field) => {
    var isExist = false;
    for (let i = 0; i < data.length; i++) {
      if (data[i][field]?.length > 0) {
        isExist = true;
      }
    }
    return isExist;
  };

  return (
    <Page
      size={
        policiesToDisplay.length > 4 && policiesToDisplay.length < 7
          ? "A2"
          : policiesToDisplay.length > 6
          ? "A1"
          : "A2"
      }
      // size={policiesToDisplay.length > 4 ? "A3" : "A4"}
      orientation={orientation}
    >
      <View /* fixed */>
        <Header
          data={personalInfoData}
          isLandscape={orientation === "landscape"}
          companyLogo={companyLogo}
          mode={mode}
        />
        <PlanDescriptionTable data={formattedData} />
        <BenefitsTable
          anotherTitle="Plan Coverage"
          title={
            <Text {...ChineseFontStyle}>
              {Locale.get("pdfDownload.planCoverage")}
            </Text>
          }
          data={formattedData.map((data) => data.benefits.planCoverages)}
          types={formattedBenefitTypes.filter(
            (benefit) =>
              ["planCoverage", "waiver"].includes(benefit.type) ||
              othersTypeLabel.includes(benefit.value)
          )}
        />
        <YearlyGuaranteedCashPaymentTable
          data={formattedData.map((data) => data.yearlyGuaranteedCashPayment)}
        />
        <BenefitsTable
          title={
            <Text {...ChineseFontStyle}>
              {Locale.get("pdfDownload.hospitalizationBenefit")}
            </Text>
          }
          data={formattedData.map(
            (data) => data.benefits.hospitalizationBenefits
          )}
          types={formattedBenefitTypes.filter(
            (benefit) => benefit.type === "hospitalization"
          )}
        />
        <MedicalCardTable
          data={formattedData.map((data) => data.medicalCard)}
        />
        <BenefitsTable
          title={
            <Text {...ChineseFontStyle}>
              {Locale.get("pdfDownload.femaleOnly")}
            </Text>
          }
          data={formattedData.map((data) => data.benefits.femaleBenefits)}
          types={formattedBenefitTypes.filter(
            (benefit) => benefit.type === "female"
          )}
        />
        <InvestmentFundsTable
          data={formattedData.map((data) => data.investmentFunds)}
        />

        {checkFieldExistInPolicy(formattedData, "nominations") && (
          <NominationTable
            data={formattedData.map((data) => data.nominations)}
            type="BENEFICIARY"
            label="Nomination"
          />
        )}
        {checkFieldExistInPolicy(formattedData, "trustee") && (
          <NominationTable
            data={formattedData.map((data) => data.trustee)}
            type="TRUSTEE"
            label="Trustee"
          />
        )}
        {checkFieldExistInPolicy(formattedData, "assignee") && (
          <NominationTable
            data={formattedData.map((data) => data.assignee)}
            type="ASSIGNEE"
            label="Assignee"
          />
        )}
        {checkFieldExistInPolicy(formattedData, "contingent") && (
          <NominationTable
            data={formattedData.map((data) => data.contingent)}
            type="CONTINGENT"
            label="Contingent"
          />
        )}

        <APLPolicyLoanTable data={formattedData} />
        <SurrenderValueTable data={formattedData} />
      </View>
    </Page>
  );
};

export default PolicyTable;
